// src/pages/ComingSoon.js
// Pre-launch landing page with ConvertKit email signup via Cloud Function
import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import LogoWhite from '../assets/divine-frameworks-logo-white.svg';

const subscribe = async (email, segment) => {
  const functions = getFunctions(undefined, 'us-west1');
  const subscribeToWaitlist = httpsCallable(functions, 'subscribeToWaitlist');
  const result = await subscribeToWaitlist({ email, segment });
  return result.data.success;
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const ComingSoon = () => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email'); // email | segment | submitting | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (!isValidEmail(email)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setErrorMsg('');
    // Check if already subscribed
    try {
      const subscribed = JSON.parse(localStorage.getItem('df_waitlist') || '[]');
      if (subscribed.includes(email.toLowerCase().trim())) {
        setStep('already');
        return;
      }
    } catch { /* ignore */ }
    setStep('segment');
  };

  const handleSegmentClick = async (segment) => {
    setStep('submitting');
    try {
      const ok = await subscribe(email, segment);
      if (ok) {
        // Remember this email locally
        try {
          const subscribed = JSON.parse(localStorage.getItem('df_waitlist') || '[]');
          subscribed.push(email.toLowerCase().trim());
          localStorage.setItem('df_waitlist', JSON.stringify(subscribed));
        } catch { /* ignore */ }
      }
      setStep(ok ? 'success' : 'error');
    } catch {
      setStep('error');
    }
  };

  return (
    <div className="cs-outer" style={{
      minHeight: '100vh',
      position: 'relative',
      overflow: 'hidden',
      background: 'linear-gradient(135deg, #7c3aed, #22d3ee, #5b21b6, #4c1d95, #bb9749, #7c3aed, #6366f1, #22d3ee, #5b21b6, #4c1d95)',
      backgroundSize: '400% 400%',
      animation: 'csAurora 18s ease infinite',
    }}>
      {/* Dark overlay — covers entire background edge-to-edge */}
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15, 10, 30, 0.55)', zIndex: 0 }} />

      {/* Radial gradient overlays */}
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 20%, rgba(124, 58, 237, 0.15) 0%, transparent 60%)', zIndex: 0 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 70% 80%, rgba(34, 211, 238, 0.08) 0%, transparent 50%)', zIndex: 0 }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 50%, rgba(187, 151, 73, 0.06) 0%, transparent 40%)', zIndex: 0 }} />

      {/* Content */}
      <div className="cs-content" style={{
        position: 'relative',
        zIndex: 1,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Montserrat', sans-serif",
        textAlign: 'center',
        padding: '3rem 2.5rem',
        boxSizing: 'border-box',
      }}>
      <div style={{ maxWidth: '700px', width: '100%' }}>

        {/* Logo */}
        <div style={{ marginBottom: '1rem' }}>
          <img src={LogoWhite} alt="Divine Frameworks" style={{ width: '260px', height: 'auto', margin: '0 auto', display: 'block' }} />
          <p style={{ fontSize: '28px', fontWeight: 300, letterSpacing: '3px', color: '#fff', textTransform: 'uppercase', marginTop: '-4px', marginBottom: '3rem' }}>Divine Frameworks</p>
        </div>

        {/* Headline */}
        <h1 className="cs-headline" style={{
          color: '#fff',
          fontSize: '28px',
          fontWeight: 500,
          lineHeight: 1.4,
          marginBottom: '1.5rem',
        }}>
          A new home for spiritual and wellness practitioners — and everyone seeking their gifts.
        </h1>

        {/* Practitioner line */}
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', lineHeight: 1.8, marginBottom: '0.75rem' }}>
          The all-in-one platform for spiritual and wellness practitioners — every tool, every resource, everything you need to run your practice, get discovered, and keep 100% of what you earn.
        </p>

        {/* Seeker line */}
        <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '17px', lineHeight: 1.8, marginBottom: '0.5rem' }}>
          Where seekers find trusted guides across spiritual, healing, wellness, and personal growth practices.
        </p>

        {/* Founder line */}
        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '17px', fontStyle: 'italic', marginBottom: '0.5rem' }}>
          Built from the ground up by someone who understands the work.
        </p>

        {/* Launching soon */}
        <p style={{ color: '#bb9749', fontSize: '15px', fontWeight: 500, letterSpacing: '1px', marginBottom: '2rem' }}>
          Launching soon.
        </p>

        {/* Signup area */}
        {step === 'already' ? (
          <p style={{
            color: '#fff',
            fontSize: '16px',
            animation: 'csFadeIn 0.5s ease',
            marginBottom: '2.5rem',
          }}>
            You're already on the list! We'll be in touch soon.
          </p>
        ) : step === 'success' ? (
          <p style={{
            color: '#fff',
            fontSize: '16px',
            animation: 'csFadeIn 0.5s ease',
            marginBottom: '2.5rem',
          }}>
            You're on the list! We'll be in touch soon.
          </p>
        ) : step === 'segment' || step === 'submitting' || step === 'error' ? (
          <div style={{ animation: 'csFadeIn 0.4s ease', marginBottom: '2.5rem' }}>
            <p style={{ color: '#fff', fontSize: '16px', marginBottom: '8px' }}>
              One quick question —
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', marginBottom: '16px' }}>
              This helps us send you the right updates.
            </p>

            {step === 'error' && (
              <p style={{ color: 'rgba(255,150,150,0.8)', fontSize: '13px', marginBottom: '12px' }}>
                Something went wrong. Please try again.
              </p>
            )}

            <div className="cs-segment-buttons" style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              {['practitioner', 'seeker'].map((s) => (
                <button
                  key={s}
                  onClick={() => handleSegmentClick(s)}
                  disabled={step === 'submitting'}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '24px',
                    fontSize: '14px',
                    cursor: step === 'submitting' ? 'default' : 'pointer',
                    fontFamily: "'Montserrat', sans-serif",
                    transition: 'all 0.2s ease',
                    background: 'transparent',
                    border: '1px solid rgba(255,255,255,0.25)',
                    color: 'rgba(255,255,255,0.6)',
                    opacity: step === 'submitting' ? 0.5 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (step !== 'submitting') {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                      e.currentTarget.style.borderColor = 'rgba(255,255,255,0.5)';
                      e.currentTarget.style.color = '#fff';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                    e.currentTarget.style.color = 'rgba(255,255,255,0.6)';
                  }}
                >
                  {step === 'submitting' ? 'Sending...' : (s === 'practitioner' ? "I'm a practitioner" : "I'm seeking guidance")}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Email step */}
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '15px', marginBottom: '0.75rem' }}>
              Get notified when we launch:
            </p>

            <form onSubmit={handleEmailSubmit} style={{ display: 'flex', maxWidth: '420px', margin: '0 auto', marginBottom: '0.5rem' }}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrorMsg(''); }}
                style={{
                  flex: 1,
                  padding: '14px 18px',
                  border: '1px solid rgba(255,255,255,0.15)',
                  borderRight: 'none',
                  borderRadius: '8px 0 0 8px',
                  background: 'rgba(255,255,255,0.08)',
                  color: '#fff',
                  fontSize: '14px',
                  fontFamily: "'Montserrat', sans-serif",
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={!email.trim()}
                style={{
                  padding: '14px 24px',
                  background: '#bb9749',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '0 8px 8px 0',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: !email.trim() ? 'default' : 'pointer',
                  fontFamily: "'Montserrat', sans-serif",
                  opacity: !email.trim() ? 0.5 : 1,
                  transition: 'opacity 0.2s ease',
                }}
              >
                Notify Me
              </button>
            </form>

            {errorMsg && (
              <p style={{ color: 'rgba(255,150,150,0.8)', fontSize: '13px', marginTop: '6px' }}>
                {errorMsg}
              </p>
            )}

            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '13px', marginTop: '8px', marginBottom: '2.5rem' }}>
              Be the first to know. No spam, ever.
            </p>
          </>
        )}

        {/* Login link */}
        <p style={{ marginBottom: '2rem' }}>
          <a
            href="/login"
            style={{
              color: 'rgba(255,255,255,0.45)',
              fontSize: '13px',
              textDecoration: 'none',
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
          >Already have an account? Log in</a>
        </p>

        {/* Instagram icon */}
        <a
          href="https://instagram.com/divineframeworks"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          style={{
            color: 'rgba(255,255,255,0.5)',
            transition: 'color 0.2s ease',
            display: 'inline-flex',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
          </svg>
        </a>
      </div>
      </div>

      {/* Animations + responsive */}
      <style>{`
        @keyframes csAurora {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes csFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 480px) {
          .cs-content { padding: 2rem 1.5rem !important; }
          .cs-headline { font-size: 22px !important; }
          .cs-segment-buttons { flex-direction: column !important; align-items: center !important; }
        }
      `}</style>
    </div>
  );
};

export default ComingSoon;
